import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { IoIosArrowForward } from "react-icons/io";
import { LuSlidersHorizontal } from 'react-icons/lu';
import { FaRegHeart, FaChevronDown } from 'react-icons/fa';
import { RiShoppingCartLine } from 'react-icons/ri';
import { MdOutlinePlaylistAdd } from 'react-icons/md';
import Rating from '../components/Rating';
import Pagination from '../components/Pagination';
import { useDispatch, useSelector } from 'react-redux';
import { price_range_product, query_products, get_category, get_category_filters } from '../store/reducers/homeReducer';
import { add_to_card, add_to_wishlist, messageClear } from '../store/reducers/cardReducer';
import { add_to_my_list, messageClear as myListMessageClear } from '../store/reducers/myListReducer';
import toast from 'react-hot-toast';
import { useCurrency } from '../context/CurrencyContext';

const SearchProducts = () => {

    let [searchParams] = useSearchParams();
    const category = searchParams.get('category');
    const searchValue = searchParams.get('value');
    const subCategory = searchParams.get('subCategory') || searchParams.get('sub');

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { products, categorys, priceRange, totalProduct, parPage, categoryFilters } = useSelector(state => state.home);
    const { userInfo } = useSelector(state => state.auth);
    const { errorMessage, successMessage } = useSelector(state => state.card);
    const { successMessage: myListSuccessMessage, errorMessage: myListErrorMessage } = useSelector(state => state.myList);
    const { formatBase } = useCurrency();

    const [pageNumber, setPageNumber] = useState(1);
    const [sortPrice, setSortPrice] = useState('');
    const [selectedFilters, setSelectedFilters] = useState({});
    const filterScrollRef = useRef(null);
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    const decodedCategory = decodeURIComponent(category || '').toLowerCase();

    const filtersKey = JSON.stringify(selectedFilters);

    const toggleFilterValue = (type, value) => {
        setSelectedFilters((prev) => {
            const next = { ...prev };
            const existing = new Set(next[type] || []);
            if (existing.has(value)) {
                existing.delete(value);
            } else {
                existing.add(value);
            }
            if (existing.size === 0) {
                delete next[type];
            } else {
                next[type] = Array.from(existing).sort((a, b) => a.localeCompare(b));
            }
            return next;
        });
        setPageNumber(1);
    };

    const resetFilters = () => {
        setSelectedFilters({});
        setPageNumber(1);
    };

    useEffect(() => {
        dispatch(price_range_product());
        dispatch(get_category());
    }, []);

    useEffect(() => {
        dispatch(get_category_filters({ category: category || '', subCategory: subCategory || '' }));
    }, [category, subCategory]);

    useEffect(() => {
        dispatch(
            query_products({
                low: priceRange.low || '',
                high: priceRange.high || '',
                category,
                subCategory,
                rating: '',
                sortPrice,
                pageNumber,
                filters: selectedFilters,
                searchValue
            })
        );
    }, [category, subCategory, pageNumber, priceRange.low, priceRange.high, sortPrice, filtersKey, searchValue]);

    useEffect(() => {
        if (successMessage) {
            toast.success(successMessage);
            dispatch(messageClear());
        }
        if (errorMessage) {
            toast.error(errorMessage);
            dispatch(messageClear());
        }
    }, [successMessage, errorMessage, dispatch]);

    useEffect(() => {
        if (myListSuccessMessage) {
            toast.success(myListSuccessMessage);
            dispatch(myListMessageClear());
        }
        if (myListErrorMessage) {
            toast.error(myListErrorMessage);
            dispatch(myListMessageClear());
        }
    }, [myListSuccessMessage, myListErrorMessage, dispatch]);

    const add_card = (id) => {
        if (userInfo) {
            dispatch(add_to_card({ userId: userInfo.id, quantity: 1, productId: id }));
        } else {
            navigate('/login');
        }
    };

    const add_wishlist = (pro) => {
        if (!userInfo) { navigate('/login'); return; }
        dispatch(add_to_wishlist({
            userId: userInfo.id,
            productId: pro._id,
            name: pro.name,
            price: pro.price,
            image: pro.images[0],
            discount: pro.discount,
            rating: pro.rating,
            slug: pro.slug
        }));
    };

    const add_my_list = (pro) => {
        if (!userInfo) { navigate('/login'); return; }
        dispatch(add_to_my_list({ userId: userInfo.id, productId: pro._id }));
    };

    const calculateFinalPrice = (price, discount) =>
        discount ? (price - (price * discount) / 100) : price;

    return (
        <div className="max-w-[1300px] w-[90%] xl:w-[95%] sm:w-[98%] mx-auto py-6">

            {/* SUBCATEGORY STRIP */}
            <section className="mt-6 bg-white">
                <div className="w-full border-t border-b py-6 relative">
                    <div className="w-[90%] mx-auto relative">
                        <button
                            onClick={() => document.getElementById('subcategory-scroll')?.scrollBy({ left: 300, behavior: 'smooth' })}
                            className="absolute right-[-20px] top-1/2 -translate-y-1/2 z-10 bg-white shadow-md w-[40px] h-[40px] rounded-full flex items-center justify-center hover:bg-slate-100"
                        >
                            <IoIosArrowForward size={20} />
                        </button>
                        <div id="subcategory-scroll" className="overflow-x-auto">
                            <div className="grid grid-rows-2 grid-flow-col gap-x-8 gap-y-6 min-w-max px-2">
                                {categorys
                                    ?.find(cat => cat.name.toLowerCase() === decodedCategory)
                                    ?.subcategories?.map((sub, index) => (
                                        <Link
                                            key={index}
                                            to={`/products/search?category=${encodeURIComponent(category)}&sub=${encodeURIComponent(sub.name)}`}
                                            className="flex flex-col items-center gap-2 group w-[110px]"
                                        >
                                            <div className="w-[100px] h-[100px] rounded-full overflow-hidden border group-hover:border-indigo-500 transition">
                                                <img src={sub.image} alt={sub.name} className="w-full h-full object-cover" />
                                            </div>
                                            <span className="text-sm font-medium text-slate-700 text-center leading-tight group-hover:text-indigo-500">
                                                {sub.name}
                                            </span>
                                        </Link>
                                    ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FILTER BAR */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
                <div className="flex items-center px-[26px]">
                    {/* Filters button — fixed left */}
                    <button
                        onClick={() => setFilterDrawerOpen(true)}
                        className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-700 pr-4 border-r border-gray-200 py-3 shrink-0 hover:text-[#059473] transition-colors"
                    >
                        <LuSlidersHorizontal size={15} />
                        Filters
                    </button>

                    {/* Sort dropdown */}
                    <div className="relative shrink-0 px-3 border-r border-gray-200 py-3">
                        <select
                            value={sortPrice}
                            onChange={e => { setSortPrice(e.target.value); setPageNumber(1); }}
                            className="appearance-none text-[13px] font-semibold text-slate-700 bg-transparent pr-5 outline-none cursor-pointer"
                        >
                            <option value="">Sort by: Relevance</option>
                            <option value="low-to-high">Price: Low to High</option>
                            <option value="high-to-low">Price: High to Low</option>
                        </select>
                        <FaChevronDown size={10} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                    </div>

                    {/* Scrollable filter pills */}
                    <div className="flex-1 relative overflow-hidden">
                        <div ref={filterScrollRef} className="flex items-center gap-2 overflow-x-auto scrollbar-hide py-3 px-3">
                            {/* Dynamic category filters */}
                            {categoryFilters.map((filter) => {
                                const selectedCount = selectedFilters[filter.type]?.length || 0;
                                return (
                                    <button
                                        key={filter.type}
                                        onClick={() => setFilterDrawerOpen(true)}
                                        className={`flex items-center gap-1 px-3 py-1 rounded-full border text-[12px] font-medium whitespace-nowrap transition-all ${selectedCount ? 'border-[#059473] text-[#059473]' : 'border-gray-300 text-slate-600 hover:border-[#059473] hover:text-[#059473]'}`}
                                    >
                                        {filter.type}{selectedCount ? ` (${selectedCount})` : ''}
                                        <FaChevronDown size={9} />
                                    </button>
                                );
                            })}

                            {/* Price range pill */}
                            {priceRange.low !== undefined && (
                                <button className="flex items-center gap-1 px-3 py-1 rounded-full border border-gray-300 text-[12px] font-medium text-slate-600 whitespace-nowrap hover:border-[#059473] hover:text-[#059473] transition-all">
                                    Price <FaChevronDown size={9} />
                                </button>
                            )}

                            {/* Discount pill */}
                            <button className="flex items-center gap-1 px-3 py-1 rounded-full border border-gray-300 text-[12px] font-medium text-slate-600 whitespace-nowrap hover:border-[#059473] hover:text-[#059473] transition-all">
                                On Sale
                            </button>

                            {/* In Stock pill */}
                            <button className="flex items-center gap-1 px-3 py-1 rounded-full border border-gray-300 text-[12px] font-medium text-slate-600 whitespace-nowrap hover:border-[#059473] hover:text-[#059473] transition-all">
                                In Stock
                            </button>
                        </div>

                        {/* Scroll right arrow */}
                        <button
                            onClick={() => filterScrollRef.current?.scrollBy({ left: 200, behavior: 'smooth' })}
                            className="absolute right-0 top-0 h-full px-2 bg-gradient-to-l from-white via-white to-transparent flex items-center"
                        >
                            <IoIosArrowForward size={16} className="text-slate-500" />
                        </button>
                    </div>

                    {/* Product count */}
                    <span className="shrink-0 pl-3 text-[12px] text-slate-400">{totalProduct} items</span>
                </div>
            </div>

            {/* FILTER DRAWER */}
            {filterDrawerOpen && (
                <div className="fixed inset-0 z-30">
                    <div
                        className="absolute inset-0 bg-black/30"
                        onClick={() => setFilterDrawerOpen(false)}
                    />
                    <div className="absolute right-0 top-0 h-full w-full max-w-[420px] bg-white shadow-xl flex flex-col">
                        <div className="flex items-center justify-between px-6 py-5 border-b">
                            <h2 className="text-2xl font-semibold text-slate-800">Filters</h2>
                            <button
                                onClick={() => setFilterDrawerOpen(false)}
                                className="text-2xl leading-none text-slate-500 hover:text-slate-800"
                                aria-label="Close filters"
                            >
                                ×
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-8">
                            {categoryFilters.length === 0 && (
                                <p className="text-sm text-slate-500">No filters available for this category.</p>
                            )}

                            {categoryFilters.map((filter) => (
                                <div key={filter.type} className="space-y-4">
                                    <h3 className="text-lg font-semibold text-slate-700">{filter.type}</h3>
                                    <div className="grid grid-cols-2 gap-3">
                                        {filter.values.map((value) => {
                                            const checked = (selectedFilters[filter.type] || []).includes(value);
                                            return (
                                                <label
                                                    key={`${filter.type}-${value}`}
                                                    className={`flex items-center gap-2 rounded-full border px-3 py-2 text-sm cursor-pointer transition ${checked ? 'border-[#059473] text-[#059473]' : 'border-gray-200 text-slate-600 hover:border-[#059473] hover:text-[#059473]'}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => toggleFilterValue(filter.type, value)}
                                                        className="accent-[#059473]"
                                                    />
                                                    <span className="truncate">{value}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="px-6 py-4 border-t flex items-center gap-3">
                            <button
                                onClick={resetFilters}
                                className="flex-1 py-2 rounded-full border border-gray-300 text-sm font-semibold text-slate-700 hover:border-[#059473] hover:text-[#059473] transition"
                            >
                                Reset
                            </button>
                            <button
                                onClick={() => setFilterDrawerOpen(false)}
                                className="flex-[2] py-2 rounded-full bg-[#ff7a00] text-white text-sm font-semibold hover:bg-[#e56f00] transition"
                            >
                                Show {totalProduct}+ results
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* PRODUCT GRID */}
            <div className="px-[26px]">
                <div className="grid grid-cols-2 mds:grid-cols-3 lgs:grid-cols-4 xls:grid-cols-5">
                    {products.map((p, i) => {
                        const finalPrice = calculateFinalPrice(p.price, p.discount);
                        return (
                            <div
                                key={i}
                                className="group bg-white p-2 overflow-hidden transition-all duration-200 hover:shadow-lg hover:-translate-y-1"
                            >
                                <Link to={`/product/details/${p.slug}`} target="_blank" rel="noopener noreferrer">
                                    <div className="relative overflow-hidden">
                                        <div className="w-full aspect-[1/1.1] overflow-hidden">
                                            <img
                                                src={p.images[0]}
                                                alt={p.name}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    </div>
                                </Link>

                                <div className="px-2 pt-1 pb-2">
                                    <h2 className="text-xs text-gray-800 line-clamp-2 leading-tight">{p.name}</h2>
                                    <div className="flex items-baseline gap-1.5 mt-1">
                                        <span className="text-[15px] font-bold text-[#ff4747]">{formatBase(finalPrice)}</span>
                                        {p.discount > 0 && (
                                            <span className="text-xs text-gray-400 line-through">{formatBase(p.price)}</span>
                                        )}
                                    </div>
                                    <div className="flex items-center mt-0.5">
                                        <Rating ratings={p.rating} />
                                    </div>
                                    {p.stock <= 5 && (
                                        <p className="text-[10px] text-red-500 font-semibold mt-0.5">Only {p.stock} left</p>
                                    )}
                                    <div className="flex gap-2 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={() => add_wishlist(p)}
                                            title="Add to Wishlist"
                                            className="flex-1 flex justify-center items-center py-1 border border-gray-300 rounded text-gray-500 hover:border-[#059473] hover:text-[#059473] transition-colors"
                                        >
                                            <FaRegHeart size={13} />
                                        </button>
                                        <button
                                            onClick={() => add_my_list(p)}
                                            title="Add to My List"
                                            className="flex-1 flex justify-center items-center py-1 border border-gray-300 rounded text-gray-500 hover:border-[#1769e0] hover:text-[#1769e0] transition-colors"
                                        >
                                            <MdOutlinePlaylistAdd size={16} />
                                        </button>
                                        <button
                                            onClick={() => add_card(p._id)}
                                            title="Add to Cart"
                                            className="flex-1 flex justify-center items-center py-1 bg-[#059473] text-white rounded hover:bg-[#04785e] transition-colors"
                                        >
                                            <RiShoppingCartLine size={13} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* PAGINATION */}
            {totalProduct > parPage && (
                <div className="px-[26px] mt-6 mb-10">
                    <Pagination
                        pageNumber={pageNumber}
                        setPageNumber={setPageNumber}
                        totalItem={totalProduct}
                        parPage={parPage}
                        showItem={Math.floor(totalProduct / parPage)}
                    />
                </div>
            )}

        </div>
    );
};

export default SearchProducts;
